import React from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { ContentHeaderUnifiedQuery } from '@confluence/content-header/entry-points/ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '@confluence/content-header/entry-points/defaultQueryVariables.js';
import { DevDocsOverwriteWarningBanner } from '@confluence/dev-docs/entry-points/DevDocsOverwriteWarningBanner.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';

import {
	useDevDocsAncestorsFragment,
	type AncestorsQueryData,
} from './useDevDocsAncestorsFragment.ts';

type AncestorsQueryVariables = {
	contentId: string;
	spaceKey?: string;
	versionOverride: null;
	embeddedContentRender: null;
	limit: number;
	includeDevDocsBylineConnection: boolean;
	status?: string[];
};

export type DevDocsOverwriteWarningBannerContainerProps = {
	contentId: string;
	spaceKey?: string;
	isLivePage: boolean;
	isUnpublishedDraft?: boolean;
};

export const DevDocsOverwriteWarningBannerContainer = ({
	contentId,
	spaceKey,
	isLivePage,
	isUnpublishedDraft,
}: DevDocsOverwriteWarningBannerContainerProps): React.JSX.Element => {
	const ancestorsFragment = useDevDocsAncestorsFragment({ contentId });
	const queryResult = useQueryInterop<AncestorsQueryData, AncestorsQueryVariables>(
		ContentHeaderUnifiedQuery,
		{
			// These variables MUST match whatever populates the cache entry for ContentHeaderUnifiedQuery
			// on the edit route: preloadEditorByLineData (SSR warm-up) and the edit byline LivePagesByLine
			// (which reads it with the same variables on a regular edit page and keeps the entry warm).
			// Because the read is cache-only, drift does NOT fire a duplicate query — it misses the cache
			// and the banner silently doesn't render. Keep these three in sync.
			variables: {
				contentId,
				spaceKey,
				versionOverride: null,
				embeddedContentRender: null,
				limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
				includeDevDocsBylineConnection: isExperimentEnabled('confluence_dev_docs'),
				...(isUnpublishedDraft && { status: ['draft'] }),
			},
			// Passive read: the banner must never trigger its own network request — the data is preloaded
			// by preloadEditorByLineData and kept warm by the edit byline's read, so a genuine miss just
			// means the banner doesn't render (fail-safe). In Apollo this is already a true no-fetch read;
			// the Relay interop currently maps 'cache-only' to 'store-or-network' (see mapFetchPolicyForQuery
			// TODO), so it behaves like 'cache-first' there until real store-only lands.
			fetchPolicy: 'cache-only',
			context: { single: true },
			skip: ancestorsFragment.isProvided,
		},
	);

	// The query ignores its errors, so the fragment ignores its field error too.
	const { data } = ancestorsFragment.isProvided ? ancestorsFragment : queryResult;

	const ancestors = data?.content?.nodes?.[0]?.ancestors ?? null;

	return <DevDocsOverwriteWarningBanner ancestors={ancestors} isLivePage={isLivePage} />;
};
